import {FC, ReactNode} from 'react'
import {useIsMobile} from '../hooks/useIsMobile'

const INK = '#1b1b29'
const MUTED = '#7e8299'

/**
 * Shared admin list toolbar: a search box plus any number of filter controls.
 * Drop it directly under the PageHead (and summary cards) on list pages.
 *
 *   <ListToolbar search={q} onSearch={setQ} placeholder='Search beds…'>
 *     <FilterSelect value={status} onChange={setStatus} options={…} />
 *   </ListToolbar>
 */
export const ListToolbar: FC<{
  search: string
  onSearch: (v: string) => void
  placeholder?: string
  children?: ReactNode
  right?: ReactNode
}> = ({search, onSearch, placeholder = 'Search…', children, right}) => {
  const isMobile = useIsMobile()
  return (
  <div
    className='ccp-fade-up'
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 10,
      alignItems: 'center',
      background: '#fff',
      borderRadius: 14,
      padding: '12px 14px',
      boxShadow: '0 6px 18px rgba(27,27,41,0.06)',
      marginBottom: 22,
    }}
  >
    <div style={{position: 'relative', flex: '1 1 240px', minWidth: 200}}>
      <svg
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke={MUTED}
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none'}}
      >
        <circle cx='11' cy='11' r='8' />
        <line x1='21' y1='21' x2='16.65' y2='16.65' />
      </svg>
      <input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={placeholder}
        style={{width: '100%', padding: '9px 32px 9px 36px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 14, color: INK, background: '#fff', outline: 'none', boxSizing: 'border-box'}}
      />
      {search && (
        <button
          onClick={() => onSearch('')}
          title='Clear'
          style={{position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#f1f3f7', color: MUTED, cursor: 'pointer', lineHeight: 1, fontSize: 13, padding: 0}}
        >
          ×
        </button>
      )}
    </div>
    {children && (isMobile
      ? <div style={{display: 'flex', flexWrap: 'wrap', gap: 10, flex: '1 1 100%', minWidth: 0}}>{children}</div>
      : children)}
    {right && <div style={{marginLeft: 'auto'}}>{right}</div>}
  </div>
  )
}

/** A single styled filter dropdown for the toolbar. */
export const FilterSelect: FC<{
  value: string
  onChange: (v: string) => void
  options: {label: string; value: string}[]
  ariaLabel?: string
}> = ({value, onChange, options, ariaLabel}) => {
  const isMobile = useIsMobile()
  return (
  <select
    aria-label={ariaLabel}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 14, fontWeight: 500, color: INK, background: '#fff', cursor: 'pointer', outline: 'none', ...(isMobile ? {flex: '1 1 130px', minWidth: 130} : {minWidth: 130})}}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
  )
}
